import { Button, Flex, Modal, Typography } from '@iheartjane/reefer';
import { Form, useForm } from '@iheartjane/reefer-hook-form';
import pickBy from 'lodash-es/pickBy';
import { Fragment, useEffect, useMemo, useState } from 'react';

import { ConfirmWrapperWithTracking } from '@jane/business-admin/components/ConfirmWrapperWithTracking';
import {
  ALL_WEIGHTS,
  COMMON_WEIGHTS,
  ModalNames,
  UNCOMMON_WEIGHTS,
  generateDefaultWeightValues,
  removeDecimals,
  restoreDecimals,
  weightConditionsToKeys,
} from '@jane/business-admin/util';
import type { WeightValueCondition } from '@jane/shared/types';

export const WeightValuesModal = ({
  closeModal,
  conditions,
  onSubmit,
}: {
  closeModal: () => void;
  conditions: WeightValueCondition[];
  onSubmit: (data: WeightValueCondition[]) => void;
}) => {
  const initiallySelectedKeys = useMemo(
    () => weightConditionsToKeys(conditions),
    [conditions]
  );

  const commonKeys = Object.keys(COMMON_WEIGHTS);
  const uncommonKeys = Object.keys(UNCOMMON_WEIGHTS);

  const FORM_ERROR_NAME = 'weight-error';
  const formMethods = useForm({
    defaultValues: {
      search: undefined,
      weights: generateDefaultWeightValues(initiallySelectedKeys),
    },
  });
  const {
    formState: { isDirty },
    setValue,
    watch,
  } = formMethods;
  const formWeights = watch('weights');
  const formSearch = watch('search');

  const [filteredCommon, setFilteredCommon] = useState(commonKeys);
  const [filteredUncommon, setFilteredUncommon] = useState(uncommonKeys);

  useEffect(() => {
    setFilteredCommon(
      formSearch
        ? commonKeys.filter((key) => key.includes(formSearch || ''))
        : commonKeys
    );
    setFilteredUncommon(
      formSearch
        ? uncommonKeys.filter((key) => key.includes(formSearch || ''))
        : uncommonKeys
    );
  }, [formSearch]);

  const onSelectAll = () => {
    setValue(
      'weights',
      generateDefaultWeightValues([
        ...truthyValues,
        ...filteredCommon,
        ...filteredUncommon,
      ])
    );
  };
  const clearSelections = () => {
    setValue('weights', generateDefaultWeightValues([]));
  };

  const truthyValues = useMemo(() => {
    return Object.keys(pickBy(formWeights, (item) => item == true)).map(
      (value) => restoreDecimals(value)
    );
  }, [JSON.stringify(formWeights)]);

  const handleSubmit = () => {
    const mappedValues = truthyValues.reduce<WeightValueCondition[]>(
      (prev: WeightValueCondition[], key: string) => {
        prev.push(ALL_WEIGHTS[key]);
        return prev;
      },
      []
    );

    onSubmit(mappedValues);
    closeModal();
  };

  return (
    <ConfirmWrapperWithTracking
      open
      setOpen={closeModal}
      hasChanges={isDirty}
      modalName={ModalNames.ProductSearch}
    >
      <Form.BaseForm
        name="product search modal"
        onSubmit={handleSubmit}
        formMethods={formMethods}
        formErrorName={FORM_ERROR_NAME}
      >
        <Modal.Header
          title="Select weight"
          actions={<Form.SubmitButton variant="primary" label="Save" ml={16} />}
        />
        <Modal.Content padding={false}>
          <Form.ErrorBanner name={FORM_ERROR_NAME} />
          <Flex px={40} pt={40} width="100%">
            <Form.SearchField
              width="100%"
              label="Search"
              name="search"
              placeholder="Search"
            />
          </Flex>
          <Flex
            flexDirection="column"
            overflowY="auto"
            height="calc(100% - 88px)"
            overflowX="hidden"
            pb={20}
            px={40}
          >
            {filteredCommon.length ? (
              <>
                <Typography variant="body-bold" mt={20} mb={12}>
                  Common weights
                </Typography>
                <Flex flexDirection="column">
                  {commonKeys.map((weight) =>
                    filteredCommon.includes(weight) ? (
                      <Fragment key={weight}>
                        <Flex justifyContent="space-between" py={12} px={20}>
                          <Typography variant="body-bold">{weight}</Typography>
                          <Form.CheckboxField
                            shouldUnregister={false}
                            label={weight}
                            name={`weights.${removeDecimals(weight)}`}
                            labelHidden
                          />
                        </Flex>
                        {filteredCommon.indexOf(weight) !==
                          filteredCommon.length - 1 && (
                          <Modal.ContentDivider padding={false} />
                        )}
                      </Fragment>
                    ) : null
                  )}
                  <Modal.ContentDivider />
                </Flex>
              </>
            ) : null}
            {filteredUncommon.length ? (
              <>
                <Typography variant="body-bold" mt={20} mb={12}>
                  Other weights
                </Typography>
                <Flex flexDirection="column">
                  {uncommonKeys.map((weight) =>
                    filteredUncommon.includes(weight) ? (
                      <Fragment key={weight}>
                        <Flex justifyContent="space-between" py={12} px={20}>
                          <Typography variant="body-bold">{weight}</Typography>
                          <Form.CheckboxField
                            shouldUnregister={false}
                            label={weight}
                            name={`weights.${removeDecimals(weight)}`}
                            labelHidden
                          />
                        </Flex>
                        {filteredUncommon.indexOf(weight) !==
                          filteredUncommon.length - 1 && (
                          <Modal.ContentDivider padding={false} />
                        )}
                      </Fragment>
                    ) : null
                  )}
                </Flex>
              </>
            ) : null}
            {!filteredCommon.length && !filteredUncommon.length && (
              <Typography pl={20} mt={20}>
                No weights found with that search term
              </Typography>
            )}
          </Flex>
        </Modal.Content>
        <Modal.Footer>
          <Flex justifyContent="space-between" alignItems="center" mt={4}>
            <Typography>{`${truthyValues.length} weights selected`}</Typography>
            <Flex gap={16}>
              <Button
                variant="tertiary"
                label="Select all"
                onClick={onSelectAll}
              />
              <Button
                variant="tertiary"
                label="Select none"
                disabled={!truthyValues.length}
                onClick={clearSelections}
              />
            </Flex>
          </Flex>
        </Modal.Footer>
      </Form.BaseForm>
    </ConfirmWrapperWithTracking>
  );
};
